/**
 * pdfPhotos — Section « Photos de l'environnement » du PDF de session.
 * Extrait de PDFExport.jsx (10.5.300, cap 300 lignes composant).
 *
 * Toutes les photos sont préparées AVANT de dessiner : le titre de section
 * n'est posé que si au moins une photo a pu être intégrée (jusqu'à 10.5.299,
 * le titre sortait seul, suivi d'un vide). Chaque photo passe par
 * imageToJpegDataUrl : jamais d'élément <img> ni de blob URL vers addImage
 * (D225, la CSP bloque la relecture du blob par jsPDF).
 *
 * Les échecs ne disparaissent plus en silence : la fonction renvoie le nombre
 * de photos ignorées et l'indisponibilité de la liste (ex. 429), que
 * PDFExport affiche en avertissement non bloquant.
 */
import * as Sentry from '@sentry/react';
import { getAuthHeaders } from '../utils/authFetch';
import { fetchProtectedBlob } from '../utils/uploadUrl';
import { drawSectionTitle, checkPageBreak, imageToJpegDataUrl } from './pdfHelpers';

// 4 emplacements photo par session (Photo.slot 1..4)
const MAX_PHOTOS = 4;
const CAPTION_H  = 8;
const ROW_GAP    = 5;

/**
 * Taille et position d'une image de ratio quelconque dans une case (contain,
 * centré) : la photo garde ses proportions au lieu du 4:3 forcé d'avant.
 */
export function fitContain(imgW, imgH, boxW, boxH) {
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { w, h, dx: (boxW - w) / 2, dy: (boxH - h) / 2 };
}

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload  = () => resolve(image);
  image.onerror = () => reject(new Error('Photo non décodable par le navigateur'));
  image.src = src;
});

// Une photo prête à poser, ou null si elle est inaccessible ou illisible.
async function preparePhoto(photo) {
  try {
    const blobUrl = await fetchProtectedBlob(`/uploads/${photo.filename}`);
    if (!blobUrl) throw new Error('Photo inaccessible (réponse non OK sur /api/media)');
    try {
      const img = await loadImage(blobUrl);
      return { ...imageToJpegDataUrl(img), caption: photo.caption };
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (err) {
    Sentry.captureException(err, { extra: { stage: 'pdf_photo' } });
    return null;
  }
}

async function fetchPhotoList(sessionId) {
  try {
    const res  = await fetch(`/api/sessions/${sessionId}/photos`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.success ? (data.photos || []) : null;
  } catch (err) {
    Sentry.captureException(err, { extra: { stage: 'pdf_photos' } });
    return null;
  }
}

/**
 * Dessine les photos de la session, deux par ligne.
 * @returns {Promise<{ yPos: number, skipped: number, unavailable: boolean }>}
 */
export async function drawSessionPhotos(pdf, {
  sessionId, title, yPos, margin, contentWidth, pageWidth, pageHeight,
}) {
  const list = await fetchPhotoList(sessionId);
  if (!list) return { yPos, skipped: 0, unavailable: true };

  const wanted = list.slice(0, MAX_PHOTOS);
  // Une à une : une photo de 24 Mpx décodée pèse ~100 Mo en mémoire.
  const ready = [];
  for (const photo of wanted) {
    const prepared = await preparePhoto(photo);
    if (prepared) ready.push(prepared);
  }
  const skipped = wanted.length - ready.length;
  if (ready.length === 0) return { yPos, skipped, unavailable: false };

  yPos = checkPageBreak(pdf, yPos, 60, margin, pageHeight);
  yPos = drawSectionTitle(pdf, title, yPos, margin);

  const cellW = (contentWidth - margin) / 2;
  const cellH = cellW * 0.75;
  let x = margin;

  ready.forEach((photo) => {
    if (x === margin) yPos = checkPageBreak(pdf, yPos, cellH + 10, margin, pageHeight);
    const fit = fitContain(photo.width, photo.height, cellW, cellH);
    pdf.addImage(photo.dataUrl, 'JPEG', x + fit.dx, yPos + fit.dy, fit.w, fit.h);
    if (photo.caption) {
      // Bandeau sur toute la largeur de la case : lisible même sur une photo portrait.
      pdf.setFillColor(40, 40, 40);
      pdf.rect(x, yPos + cellH - CAPTION_H, cellW, CAPTION_H, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.text(pdf.splitTextToSize(photo.caption, cellW - 4)[0], x + 2, yPos + cellH - 2);
    }
    x += cellW + margin;
    if (x >= pageWidth - margin) { x = margin; yPos += cellH + ROW_GAP; }
  });
  if (x > margin) yPos += cellH + ROW_GAP;

  return { yPos, skipped, unavailable: false };
}
